// Per-channel offline message (`config.offline_message`). Defaults resolved at read
// time — never written into configs, so a platform rewording reaches everyone.
// CLIENT-SAFE: imported by "use client" components — the sender lives in
// offline-message-send.ts (server graph).

import type { ChannelOfflineMessageConfig, MessageChannel } from '../../types'
import type { InboundStoreOnlyReason } from './resolve-inbound-agent'

export const DEFAULT_OFFLINE_MESSAGE =
  "We're offline right now. Your message has been received and we'll reply as soon as we can."

/** Marks the stored message as the offline auto-reply, so the inbox can label it. */
export const OFFLINE_MESSAGE_PAYLOAD_KEY = 'offline_message'

/** Conversation-metadata key: one offline send per session, and which message it was. */
export const OFFLINE_MESSAGE_SENT_KEY = 'offline_message_sent'

export interface OfflineMessageSentMarker {
  session_number?: number | null
  message_id?: string
  sent_at?: string
}

export interface ResolvedOfflineMessage {
  enabled: boolean
  text: string
}

// enabled defaults: web_widget true, others false (they SEND, so opt-in); web_demo never.
export function resolveOfflineMessage(
  channel: Pick<MessageChannel, 'channel_type' | 'config'>,
): ResolvedOfflineMessage {
  if (channel.channel_type === 'web_demo') {
    return { enabled: false, text: DEFAULT_OFFLINE_MESSAGE }
  }
  const config = (channel.config as { offline_message?: ChannelOfflineMessageConfig } | null)
    ?.offline_message
  const enabled = config?.enabled ?? (channel.channel_type === 'web_widget')
  const custom = typeof config?.text === 'string' ? config.text.trim() : ''
  return { enabled, text: custom || DEFAULT_OFFLINE_MESSAGE }
}

/**
 * «Offline message answers on N of M channels», one definition for every surface. `web_demo` is
 * out of both counts — it can never carry an offline message, so counting it only inflates M.
 *
 * `agentDisabled` mirrors `resolveOfflineSendCause`: a disabled agent makes every enabled channel
 * answer, while a live agent delivers only where the channel's OWN AI replies switch is off.
 * Required, because the same channels give two different answers and a silent default would lie.
 */
export function summarizeOfflineChannels(
  channels: Array<{
    channel_type: MessageChannel['channel_type']
    status?: string
    ai_replies_enabled?: boolean
    /** Optional on the client row type; normalized here so callers stop repeating `?? {}`. */
    config?: Record<string, unknown> | null
  }>,
  { agentDisabled }: { agentDisabled: boolean },
): { connected: number; offline: number } {
  const connected = channels.filter(
    (c) => c.status === 'connected' && c.channel_type !== 'web_demo',
  )
  return {
    connected: connected.length,
    offline: connected.filter(
      (c) =>
        (agentDisabled || c.ai_replies_enabled === false) &&
        resolveOfflineMessage({ channel_type: c.channel_type, config: c.config ?? {} }).enabled,
    ).length,
  }
}

export type OfflineSendCause = 'channel_toggle' | 'agent_disabled'

// One rule for both delivery paths: nobody is answering on this channel — its AI
// toggle is off, or the whole agent is disabled. A `draft` agent never qualifies.
export function resolveOfflineSendCause(params: {
  channelAiOff: boolean
  storeOnlyReason?: InboundStoreOnlyReason
}): OfflineSendCause | null {
  if (params.storeOnlyReason === 'agent_disabled') return 'agent_disabled'
  if (params.storeOnlyReason !== undefined) return null
  return params.channelAiOff ? 'channel_toggle' : null
}
